import { DialogContent, DialogTitle, Grid } from '@mui/material';
import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  VendorDocumentDTO,
  VendorDocumentCategoryDTO,
  IFile,
  createVendorDocument,
  IUserDTO,
} from 'wisestep-api';
import {
  WsDataTable,
  WsDialog,
  WsFilePreview,
  EmptyTeamsAndUsersIcon,
  formattedDateISO,
  getDateUsingFormat,
  WsListingLoader,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useUserContext } from '../../../context/UserContext/UserContext';
import AddEntityButton from '../../../features/clientDetails/components/addEntityButton/AddEntityButton';
import UploadFile from '../../../features/clientDetails/components/uploadFile';
import { downloadCV } from '../../../features/conversationWidget/utils/cvHelper';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { vendorDocumentPayload } from '../helper/payload';
import useVendorFilesByVendorId from '../helper/useVendorFilesByVendorId';

interface IComponentProps {
  vendorId: string;
}

const VendorDocumentsList = ({ vendorId }: IComponentProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const methods = useForm<VendorDocumentCategoryDTO>({
    defaultValues: { name: '' },
  });
  const [refresh, setRefresh] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const documents = useVendorFilesByVendorId(vendorId, refresh, setLoader);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState<boolean>(false);

  const columns = [
    {
      Header: 'File Name',
      accessor: 'name',
    },
    {
      Header: 'Uploaded By',
      accessor: 'uploadedBy',
    },
    {
      Header: 'Upload Date',
      accessor: 'date',
    },
    {
      Header: '',
      accessor: 'details',
      Cell: (cell: Cell) => (
        <WsFilePreview
          url={cell.value?.location}
          requireDownloadIcon={true}
          hideViewIcon={true}
          onDownload={() => downloadCV(cell.value?.s3Url, cell.value?.name)}
        />
      ),
    },
  ];
  const data = useMemo(
    () =>
      documents?.map((file) => ({
        name: file?.name,
        uploadedBy: file?.uploadedBy,
        date:
          file?.uploadedOn != undefined
            ? getDateUsingFormat(file.uploadedOn?.toString(), 'DD-MMM-YY')
            : '',
        details: file,
      })),
    [documents]
  );

  const removeQueryParams = (url: string) => {
    const urlObj = new URL(url);
    urlObj.search = '';
    return urlObj.toString();
  };

  const handleDialog = (value: boolean) => setOpenUploadFileDialog(value);

  const saveFile = async (file: IFile) => {
    const payload: VendorDocumentDTO = { ...vendorDocumentPayload };
    try {
      if (file.location) {
        payload.id = '';
        payload.vendor = {
          id: vendorId,
          name: '',
          email: '',
          registrationId: '',
          location: null,
          active: true,
        };
        payload.name = file.name;
        payload.s3Url = removeQueryParams(file.location);
        payload.uploadedBy = `${loggedInUser.firstName} ${loggedInUser.lastName ?? ''}`;
        payload.uploadedOn = formattedDateISO(new Date());
        const res = await createVendorDocument(payload);
        if (res) {
          setRefresh(!refresh);
          handleDialog(false);
          setNotification(success('File has been saved successfully !!'));
        }
      }
    } catch {
      setNotification(failed('Failed to save file, Please try again !!'));
    }
  };

  return (
    <FormProvider {...methods}>
      <Grid item xs={12} mt={3}>
        {documents?.length > 0 && !loader ? (
          <>
            <AddEntityButton
              onClick={() => handleDialog(true)}
              justifyContent="flex-end"
              buttonText="Upload File"
            />
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <WsDataTable columns={columns} data={data ?? []} selectable={true} dense={false} />
            </ErrorBoundary>
          </>
        ) : loader ? (
          <WsListingLoader />
        ) : (
          <>
            {
              <NoDataFoundCard
                text={'No Documents available for the vendor'}
                icon={<EmptyTeamsAndUsersIcon />}
                subElement={
                  <AddEntityButton
                    buttonText="Upload File"
                    onClick={() => handleDialog(true)}
                    justifyContent="center"
                  />
                }
              />
            }
          </>
        )}
      </Grid>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <WsDialog openDialog={openUploadFileDialog} onClose={() => handleDialog(false)}>
          <DialogTitle>{'Add File'}</DialogTitle>
          <DialogContent>
            <UploadFile onCancel={() => handleDialog(false)} onContinue={saveFile} />
          </DialogContent>
        </WsDialog>
      </ErrorBoundary>
    </FormProvider>
  );
};

export default VendorDocumentsList;
